import {
    Component,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '../auth/auth.service';
import { CoachingEventStatus } from '../coaching-base/general';
import { actions, addPageAction } from '../coaching/actions';
import { StateToProps } from '../store';
import { useDispatcher, UseStore } from '../store.decorator';

import {
    clearState,
    reload,
    repCardActions,
    sortByColumn,
} from './coaching-card-rep.action';
import { TranslationKeyMap } from './coaching-card-rep.i18n';
import { coachingCardRepReducer } from './coaching-card-rep.reducer';
import { CoachingCardRepState } from './coaching-card-rep.state';
import { CoachingTypeRepCard } from './constant/Coaching-type-rep.constant';
import { RepCardActions } from './enums/card-actions.enum';
import { CardItemType } from './enums/card-item-type.enum';
import { SortableHeaderDirective } from './helper/sortable-table-header.directive';
import { AccordionChildItem } from './models/accordion-child-item.model';
import { CoachingType } from './models/coaching-type.model';
import { SortEvent } from './models/sort-event-model';

@Component({
    selector: '[cmpsCoachingCardRep]',
    templateUrl: './coaching-card-rep.component.html',
})
@UseStore<CoachingCardRepState>('coachingCardRep', coachingCardRepReducer)
export class CoachingCardRepComponent
    implements OnInit, OnDestroy, StateToProps<CoachingCardRepState>
{
    state: CoachingCardRepState;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _isExpanded: boolean;
    /** @private */ _cardItemType = CardItemType;
    /** @private */ _coachingType = CoachingType;
    /** @private */ _repCardActions = RepCardActions;
    /** @private */ _coachingTypeRepCard = CoachingTypeRepCard;

    @ViewChildren(SortableHeaderDirective)
    headers: QueryList<SortableHeaderDirective>;

    constructor(
        private readonly _authService: AuthService,
        private readonly _router: Router,
    ) {
        this._isExpanded = false;
    }

    ngOnInit(): void {
        this._onReload();
    }

    ngOnDestroy(): void {
        useDispatcher(this, clearState({}));
    }

    /** @private */
    _onExpandClick(): void {
        addPageAction(actions.CoachingCardRep.EXPAND_CLICK);

        this._isExpanded = !this._isExpanded;
        const cardElement =
            document.getElementsByClassName('coaching-card-rep')[0]
                .parentElement.classList;
        cardElement.toggle('insight-component-item--expanded');
    }

    /** @private */
    _onReload(): void {
        addPageAction(actions.CoachingCardRep.RELOAD_CLICK);

        useDispatcher(
            this,
            reload({
                userId: this._authService.user.id,
                periodTypeId: 4,
            }),
        );
    }

    /** @private */
    _navigate(item: AccordionChildItem, isActivePlan: boolean): void {
        addPageAction(actions.CoachingCardRep.NAVIGATE_CLICK, {
            item: item?.commitmentId,
            isActivePlan,
        });

        const userId = this._authService.user.id;
        const urlForDraft =
            item.commitmentId !== 0
                ? `/coaching-detail/${userId}/commitments/${item.commitmentId}/drafts/${item.draftId}/approvals/${item.approvalId}`
                : `/coaching-detail/${userId}/draftcommitments/${item.draftId}/approvals/${item.approvalId}`;

        const cmds =
            isActivePlan != null ||
            item.statusId === CoachingEventStatus.DISPUTED
                ? `coaching-detail/${userId}/commitments/${item.commitmentId}`
                : urlForDraft;
        if (!isActivePlan && item.approvalId != null) {
            cmds.concat('approvals', item.approvalId);
        }
        void this._router.navigate([cmds], {
            queryParams: {
                fromInsight: isActivePlan ? null : true,
            },
        });
    }

    /** @private */
    _onMetricClick(metricId?: number): void {
        addPageAction(actions.CoachingCardRep.METRIC_CLICK, { metricId });

        void this._router.navigate(
            ['coaching-detail', this._authService.user.id],
            {
                queryParams: {
                    metricId,
                    fromOA: false,
                },
                queryParamsHandling: 'merge',
            },
        );
    }

    /** @private */
    _onSort({ column, direction, index }: SortEvent): void {
        addPageAction(actions.CoachingCardRep.SORT_CLICK, {
            column,
            direction,
            index,
        });

        // resetting other headers
        this.headers.forEach((header) => {
            if (header.sortable !== column) {
                header.direction = 'asc';
            }
        });

        useDispatcher(
            this,
            sortByColumn({
                index,
                column,
                direction,
            }),
        );
    }

    /** @private */
    _toggleAccordion(index: number): void {
        addPageAction(actions.CoachingCardRep.ACCORDION_TOGGLE_CLICK, {
            index,
        });

        useDispatcher(
            this,
            repCardActions({
                actionType: RepCardActions.ACCORDION_TOGGLE,
                index,
            }),
        );
    }

    /** @private */
    _onShowMoreClick(index: number): void {
        addPageAction(actions.CoachingCardRep.SHOW_MORE_TOGGLE_CLICK, {
            index,
        });

        useDispatcher(
            this,
            repCardActions({
                actionType: RepCardActions.SHOW_MORE_TOGGLE,
                index,
            }),
        );
    }

    /** @private */
    _getTooltipData(e: HTMLElement, text: string): string {
        if (
            e != null &&
            (e.offsetWidth < e.scrollWidth || e.offsetHeight < e.scrollHeight)
        ) {
            return text;
        }
        return '';
    }
}
